import { useMemo } from "react";

import {
    BestQuote,
    AbfLoanExpanded,
    RefinanceOption,
    getOffsetDetailsFromStrategyDuration,
    isStrategyDurationSame,
    calculateHealthRatio,
    getLoanCollateralMarketUsdValue,
    getLoanCollateralUsdValue,
    summarizeLedgerAccount,
    TokenBalanceExpanded,
    getZcLoanLtv,
    getZcLedger,
    isLoanDefaulted,
    isLoanLiquidated
} from "@bridgesplit/abf-react";
import { StrategyDuration } from "@bridgesplit/abf-sdk";
import { bsMath } from "@bridgesplit/utils";

export function calculateOptionsWithOffsets({
    quotes,
    strategyDurations,
    newPrincipalAmount,
    loanExpanded
}: {
    quotes: BestQuote[] | undefined;
    strategyDurations: StrategyDuration[] | undefined;
    newPrincipalAmount: number;
    loanExpanded: AbfLoanExpanded | undefined;
}): RefinanceOption[] | undefined {
    if (!quotes || !strategyDurations) return undefined;
    return strategyDurations.map((d): RefinanceOption => {
        const { loanEndDate } = getOffsetDetailsFromStrategyDuration(d);
        const bestQuote = quotes.find((q) => isStrategyDurationSame(q, d));

        const principalUsd = bsMath.mul(newPrincipalAmount, loanExpanded?.principalUsdPrice) ?? 0;
        const collateralUsd = getLoanCollateralUsdValue(loanExpanded);

        const healthRatio = calculateHealthRatio(principalUsd, collateralUsd, bestQuote?.liquidationThreshold);
        const ltv = principalUsd / collateralUsd;
        return { loanEndDate, bestQuote, totalDebt: newPrincipalAmount, strategyDuration: d, healthRatio, ltv };
    });
}

export function useCollateralWithMaxWithdrawableAmounts(
    loanExpanded: AbfLoanExpanded | undefined,
    additionalRepayAmount?: number
) {
    return useMemo(() => {
        const ledger = getZcLedger(loanExpanded);
        const totalOutstanding = loanExpanded ? summarizeLedgerAccount(ledger).totalOutstanding : 0;
        // Calculate total outstanding after the repay
        const totalOutstandingAfterRepay = Math.max(totalOutstanding - (additionalRepayAmount ?? 0), 0);
        const loanLtv = getZcLoanLtv(loanExpanded) ?? 1;
        const totalOutstandingUsd = totalOutstandingAfterRepay * (loanExpanded?.principalUsdPrice ?? 0);
        const requiredValueUsd = totalOutstandingUsd / loanLtv;
        const totalCollateralUsd = getLoanCollateralMarketUsdValue(loanExpanded);

        if (
            loanExpanded &&
            (totalOutstandingAfterRepay === 0 || isLoanDefaulted(loanExpanded) || isLoanLiquidated(loanExpanded))
        ) {
            return {
                collateralWithMaxWithdrawableAmounts: loanExpanded.collateral.map((collateral) => ({
                    key: collateral.metadata.mint,
                    metadata: collateral.metadata,
                    amount: collateral.amount
                })),
                minRepayAmount: 0
            };
        }

        const withdrawableAmounts = loanExpanded?.collateral.map(
            (collateral): { maxWithdrawableAmount: TokenBalanceExpanded; minRepayAmount: number } => {
                const collateralUsdPrice = collateral.usdPrice ?? 0;
                const excessCollateralUsd = Math.max(totalCollateralUsd - requiredValueUsd, 0);
                const withdrawableAmount = collateralUsdPrice > 0 ? excessCollateralUsd / collateralUsdPrice : 0;
                const finalAmount = Math.min(collateral.amount, withdrawableAmount);
                const remainingCollateralUsd = totalCollateralUsd - collateral.amount * collateralUsdPrice;
                const maxDebtUsd = remainingCollateralUsd * loanLtv;
                const minRepayAmountUsd = Math.max(totalOutstandingUsd - maxDebtUsd, 0);
                const minRepayAmount = loanExpanded.principalUsdPrice
                    ? minRepayAmountUsd / loanExpanded.principalUsdPrice
                    : 0;
                return {
                    maxWithdrawableAmount: {
                        key: collateral.metadata.mint,
                        metadata: collateral.metadata,
                        amount: finalAmount
                    },
                    minRepayAmount: minRepayAmount ?? 0
                };
            }
        );
        const minRepayAmounts = withdrawableAmounts?.map((w) => w.minRepayAmount) ?? [];
        return {
            collateralWithMaxWithdrawableAmounts: withdrawableAmounts?.map((w) => w.maxWithdrawableAmount),
            minRepayAmount: minRepayAmounts.length > 0 ? Math.min(...minRepayAmounts) : 0
        };
    }, [loanExpanded, additionalRepayAmount]);
}
